import { CellStack, StateBlock } from "@/components/layout-2/ds";
import { DenseTable } from "@/components/layout-2/ds-settings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatParis } from "@/utils/dialer-datetime";
import { CircleHelp } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { feedingUrl } from "./api";
import type { FeedingLine, FeedingReason } from "./types";
import { WhyInline } from "./why-panel";

export function ReasonBadge({ reason }: { reason: Pick<FeedingReason, "label" | "tone"> }) {
    return (
        <Badge tone={reason.tone} className="max-w-full truncate" title={reason.label}>
            {reason.label}
        </Badge>
    );
}

/**
 * The decision taken for each record of a pass (or of its preview), with "Why?" on every line. The
 * explanation opens as a pane on the right of the table, inside the same dialog. The caller filters `lines`.
 */
export function LinesTable({
    lines,
    campaignCode,
    runId,
    base,
    footer
}: {
    lines: FeedingLine[];
    campaignCode: string;
    runId: number;
    /** Tab URL the links of the explanation are relative to. */
    base: string;
    /** Under the table: "Load more". */
    footer?: React.ReactNode;
}) {
    const [open, setOpen] = useState<FeedingLine | null>(null);
    return (
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto" data-testid="lines">
                {lines.length === 0 ? (
                    <StateBlock message="No record with this reason." />
                ) : (
                    <DenseTable<FeedingLine>
                        rows={lines}
                        itemToId={line => String(line.id)}
                        primaryColumn="record"
                        columns={[
                            {
                                name: "Record",
                                key: "record",
                                size: 200,
                                cellFn: line => (
                                    <Link className="text-blue-700 hover:text-blue-900 hover:underline" to={`/${line.object.url}/${line.object.id}`}>
                                        {line.object.name}
                                    </Link>
                                )
                            },
                            { name: "Account · Contact", key: "account", size: 180, cellFn: line => <CellStack primary={line.account} secondary={line.contact ?? "No primary contact"} /> },
                            {
                                name: "Outcome",
                                key: "reason",
                                size: 200,
                                cellFn: line => (
                                    <CellStack
                                        primary={<ReasonBadge reason={line.reason} />}
                                        secondary={line.eligible_at ? `Eligible again ${formatParis(line.eligible_at, "dd/MM/yyyy")}` : line.reason.temporary ? "Retried at a later pass" : undefined}
                                        secondaryTone={line.eligible_at ? "warning" : undefined}
                                    />
                                )
                            },
                            {
                                name: "",
                                key: "why",
                                size: 96,
                                cellFn: line => (
                                    <Button type="button" variant={open?.id === line.id ? "secondary" : "ghost"} size="sm" onClick={() => setOpen(line)} data-testid={`why-${line.id}`}>
                                        <CircleHelp className="mr-1 h-4 w-4" />
                                        Why?
                                    </Button>
                                )
                            }
                        ]}
                    />
                )}
                {footer}
            </div>
            {open && (
                <WhyInline
                    url={feedingUrl(campaignCode, `runs/${runId}/lines/${open.id}/why/`)}
                    base={base}
                    subject={`${open.object.name}${open.account ? ` · ${open.account}` : ""}${open.contact ? ` · ${open.contact}` : ""}`}
                    onClose={() => setOpen(null)}
                />
            )}
        </div>
    );
}
